import { useEffect, useMemo, useState } from 'react';
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted
} from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import { DmaMap } from '@Components/DmaMap/DmaMap';
import { GradientLegend } from '@Components/GradientLegend';
import { ConvergentColorMap6 as GEO_MAP_COLORS, Neutral300 } from '../../../../utils/colors';
import { GeoDmaValue } from '../data-qa-types';

interface DmaChoroplethProps {
  data: GeoDmaValue[];
  formatValue: (value: number) => string;
  // Show the built-in stat row below the map. Off when the caller renders its own
  // KPI tiles above the map (Dependent Variable geo block).
  showStats?: boolean;
  // Reports the resolved DMA-code -> name map once the CDN fetch below completes, so a
  // caller with showStats=false (i.e. rendering its own "Top DMA" tile from the same
  // `data`) can resolve a real name too, instead of duplicating this fetch a third time
  // (DmaMap already does it once; see DMA_NAMES_URL's own comment).
  onDmaNamesLoaded?: (names: Record<string, string>) => void;
}

interface DmaPathEntry {
  path: string;
  name: string;
}

const isDmaPathEntries = (value: unknown): value is Record<string, DmaPathEntry> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every(
    entry =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as DmaPathEntry).name === 'string'
  );

// Same CDN asset DmaMap fetches for path data -- this is a second, independent fetch
// for just the name field, since DmaMap doesn't expose its own resolved paths back to
// the caller. Mirrors GeoMMM.tsx's own small "extract .name into a flat map" pattern.
const DMA_NAMES_URL = 'https://cdn.blisspointmedia.com/geo/DmaPaths.json';

// Reuses the shared DmaMap (zoom/pan, real tooltip overlay) and GradientLegend +
// std-dev threshold binning already proven on the GeoMMM Results page
// (GeographicAnalysis.tsx) rather than this feature's own hand-rolled inline-SVG
// renderer and log-scale legend -- same map experience, just for the dependent
// variable's raw per-DMA total instead of model output attribution.
export const DmaChoropleth = ({
  data,
  formatValue,
  showStats = true,
  onDmaNamesLoaded
}: DmaChoroplethProps) => {
  const [dmaNames, setDmaNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(DMA_NAMES_URL);
        if (!res.ok) return;
        const json: unknown = await res.json();
        if (!active || !isDmaPathEntries(json)) return;
        const names: Record<string, string> = {};
        for (const [code, entry] of Object.entries(json)) {
          names[code] = entry.name;
        }
        setDmaNames(names);
        onDmaNamesLoaded?.(names);
      } catch {
        // Names are tooltip-only labeling -- fine to silently fall back to bare DMA
        // codes. DmaMap surfaces its own error (via useSetError) if the path data
        // itself fails to load.
      }
    })();
    return () => {
      active = false;
    };
    // Intentionally fetch once on mount regardless of onDmaNamesLoaded's identity --
    // this isn't meant to re-fetch just because a caller re-renders with a new inline
    // callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueByDma = useMemo(() => {
    const map: Record<string, number> = {};
    for (const d of data) map[d.dmaCode] = d.value;
    return map;
  }, [data]);

  const thresholds = useMemo(
    () => (data.length > 0 ? getThresholdBinsWithStdDevNonAdjusted(data.map(d => d.value)) : []),
    [data]
  );

  const renderColor = (dma: string): string => {
    const value = valueByDma[dma];
    if (value === undefined || thresholds.length === 0) return Neutral300;
    return getColorForIndexedVolume(value, thresholds, GEO_MAP_COLORS);
  };

  const renderTooltip = (dma: string) => {
    const value = valueByDma[dma];
    const name = dmaNames[dma] ?? `DMA ${dma}`;
    return (
      <div className='data-qa-geo-map__tooltip'>
        <div className='data-qa-geo-map__tooltip-header'>
          {name} (DMA {dma})
        </div>
        <div className='data-qa-geo-map__tooltip-value'>
          {value === undefined ? 'No data' : formatValue(value)}
        </div>
      </div>
    );
  };

  const stats = useMemo(() => {
    const withValue = data.filter(d => d.value > 0);
    const total = withValue.reduce((sum, d) => sum + d.value, 0);
    const sorted = [...withValue].sort((a, b) => b.value - a.value);
    const top = sorted[0];
    const top3 = sorted.slice(0, 3).reduce((sum, d) => sum + d.value, 0);
    return {
      activeCount: withValue.length,
      topName: top ? (top.dmaName ?? dmaNames[top.dmaCode] ?? `DMA ${top.dmaCode}`) : '—',
      top3Share: total > 0 ? (top3 / total) * 100 : 0
    };
  }, [data, dmaNames]);

  return (
    <div className='data-qa-geo-map'>
      {thresholds.length > 0 ? (
        <GradientLegend bins={thresholds} useConvergent formatLabel={formatValue} />
      ) : (
        <div className='data-qa-geo-map__legend--empty'>No positive values to map.</div>
      )}
      <div className='data-qa-geo-map__body'>
        <DmaMap enableZoom renderColor={renderColor} renderTooltip={renderTooltip} />
        {showStats && (
          <div className='data-qa-geo-map__stats'>
            <Kpi label='DMAs with data' value={String(stats.activeCount)} />
            <Kpi label='Top DMA' value={stats.topName} />
            <Kpi label='Top-3 concentration' value={`${stats.top3Share.toFixed(1)}%`} />
          </div>
        )}
      </div>
    </div>
  );
};

const Kpi = ({ label, value }: { label: string; value: string }) => (
  <div className='data-qa-geo-map__kpi'>
    <div className='data-qa-geo-map__kpi-value'>{value}</div>
    <div className='data-qa-geo-map__kpi-label'>{label}</div>
  </div>
);
